import { OMS_COLORS, tint } from '../../dashboard/omsDashTheme';
import { USAGE_COLOR } from '../adminTheme';
import { USAGE_META, type UsageClass } from '../utils/adminMetrics';

/** One usage-trend chip style everywhere (spec vocabulary, exact). */
export function UsageChip({ usage }: { usage: UsageClass }) {
  const palette = USAGE_COLOR[usage];
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.08em]"
      style={{ color: palette.ink, backgroundColor: tint(palette.fill, 0.14) }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: palette.fill }} />
      {USAGE_META[usage].label}
    </span>
  );
}

/** "▲ 12% vs last month" — every delta names its comparison window. */
export function DeltaBadge({
  delta,
  phrase,
  suffix = '',
}: {
  delta: number | null;
  phrase: string;
  suffix?: string;
}) {
  if (delta === null) {
    return (
      <span className="text-[10px] font-semibold" style={{ color: OMS_COLORS.caption }}>
        no prior data
      </span>
    );
  }
  const up = delta > 0;
  const flat = delta === 0;
  const color = flat ? OMS_COLORS.slate : up ? '#1F5A47' : '#9F1239';
  return (
    <span className="text-[10px] font-extrabold tabular-nums" style={{ color }}>
      {flat ? '— level' : `${up ? '▲' : '▼'} ${Math.abs(delta)}${suffix}`} {phrase}
    </span>
  );
}

/** Tiny inline sparkline; draws once, no chart library. */
export function MiniSpark({
  values,
  width = 84,
  height = 22,
  stroke = '#14606B',
  label,
}: {
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  label: string;
}) {
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const points = values
    .map((v, i) => `${(i / (values.length - 1)) * width},${height - (v / max) * (height - 3) - 1.5}`)
    .join(' ');
  return (
    <svg width={width} height={height} role="img" aria-label={label} className="shrink-0">
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth={1.6}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Rate-trio micro bar: completed / pending / skipped shares of scheduled. */
export function RateTrioBar({
  completionPct,
  pendingPct,
  skipPct,
  height = 8,
}: {
  completionPct: number | null;
  pendingPct: number | null;
  skipPct: number | null;
  height?: number;
}) {
  if (completionPct === null || pendingPct === null || skipPct === null) {
    return (
      <div
        className="w-full rounded-full"
        style={{ height, backgroundColor: 'rgba(11,59,46,0.08)' }}
        aria-label="No scheduled tasks"
      />
    );
  }
  return (
    <div
      className="flex w-full overflow-hidden rounded-full"
      style={{ height, backgroundColor: 'rgba(11,59,46,0.08)' }}
      role="img"
      aria-label={`${completionPct}% completed, ${pendingPct}% pending, ${skipPct}% skipped of scheduled`}
    >
      <div style={{ width: `${completionPct}%`, backgroundColor: '#339274' }} title={`Completed ${completionPct}%`} />
      <div style={{ width: `${pendingPct}%`, backgroundColor: '#C9A961' }} title={`Pending ${pendingPct}%`} />
      <div style={{ width: `${skipPct}%`, backgroundColor: '#5A6275' }} title={`Skipped ${skipPct}%`} />
    </div>
  );
}

/** Legend that always sits adjacent to any rate-trio visual. */
export function RateTrioLegend() {
  const items = [
    { label: 'Completed', color: '#339274' },
    { label: 'Pending', color: '#C9A961' },
    { label: 'Skipped', color: '#5A6275' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1 text-[10px] font-bold" style={{ color: OMS_COLORS.caption }}>
          <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}
